import { useEffect, useState } from 'react';
import {
     collection,
     query,
     orderBy,
     onSnapshot,
     where,
     documentId
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { ChatBubble, DynamicFeed, EditNote, Lightbulb, RocketLaunch, Share, StarBorderPurple500, ThumbUpAlt, Whatshot, BookmarkBorder, AdminPanelSettings } from '@mui/icons-material';
import { formatCreatedAt, shareTextOf } from '../utils/Universalfunctions';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import { Link } from 'react-router-dom';
import Loading from '../component/Loading';
import { useAuth } from '../config/AuthContext';
import EditProblemOffcanvas from '../component/EditProblemOffcanvas';
import PageShapes from '../component/PageShapes';
import { ADMIN_UID } from '../config/config';
import AdminCard from '../component/AdminCard';

const OverView = () => {
     const userId = JSON.parse(localStorage.getItem('userID'));
     const { user, userData } = useAuth();

     const [problems, setProblems] = useState([]);
     const [likedProblems, setLikedProblems] = useState([]);
     const [bookmarkedProblems, setBookmarkedProblems] = useState([]);
     const [comments, setComments] = useState([]);
     const [editItem, setEditItem] = useState(null);

     const [totalProblems, setTotalProblems] = useState(0);
     const [loading, setLoading] = useState(true);
     const [totalInterested, setTotalInterested] = useState(0);
     const [allProblems, setAllProblems] = useState([]);
     const isAdmin = userData?.role === ADMIN_UID;


     // 🔥 COMMON FETCH BY IDS
     const fetchProblemsByIds = (ids, setState) => {
          if (!ids.length) {
               setState([]);
               return;
          }

          const q = query(
               collection(db, 'problems'),
               where(documentId(), 'in', ids.slice(0, 10))
          );

          return onSnapshot(q, (snap) => {
               const list = snap.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
               }));
               setState(list);
          });
     };

     // ================= PROBLEMS =================
     useEffect(() => {
          const q = query(collection(db, 'problems'), orderBy('createdAt', 'desc'));

          const unsubscribe = onSnapshot(q, (snapshot) => {
               const allDocs = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
               }));

               setTotalProblems(allDocs.length);

               const filtered = allDocs.filter(item => item.uid === userId);
               setProblems(filtered);

               // Admin — all problems
               setAllProblems(allDocs);

               const total = filtered.reduce((acc, item) => {
                    return acc + (item.interestedCount || 0);
               }, 0);

               setTotalInterested(total);

               setLoading(false);
          });

          return () => unsubscribe();
     }, [userId]);

     // ================= LIKED =================
     useEffect(() => {
          const q = query(
               collection(db, 'likes'),
               where('userId', '==', userId)
          );

          let unsubProblems;

          const unsub = onSnapshot(q, (snap) => {
               const ids = snap.docs.map(d => d.data().problemId);

               if (unsubProblems) unsubProblems();
               unsubProblems = fetchProblemsByIds(ids, setLikedProblems);
          });

          return () => {
               unsub();
               if (unsubProblems) unsubProblems();
          };
     }, [userId]);

     // ================= BOOKMARK =================
     useEffect(() => {
          const q = query(
               collection(db, 'bookmarks'),
               where('userId', '==', userId)
          );

          let unsubProblems;

          const unsub = onSnapshot(q, (snap) => {
               const ids = snap.docs.map(d => d.data().problemId);

               if (unsubProblems) unsubProblems();
               unsubProblems = fetchProblemsByIds(ids, setBookmarkedProblems);
          });

          return () => {
               unsub();
               if (unsubProblems) unsubProblems();
          };
     }, [userId]);

     // ================= COMMENTS =================
     useEffect(() => {
          const q = query(
               collection(db, 'comments'),
               where('userId', '==', userId),
               orderBy('createdAt', 'desc')
          );

          const unsub = onSnapshot(q, (snap) => {
               setComments(snap.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
               })));
          });

          return () => unsub();
     }, [userId]);


     // ================= OLD CARD (UNCHANGED) =================
     const renderProblemCard = (item, i) => (
          <div className='col-md-4' key={i}>
               <div className={`data-row ${item.type === 'Idea' ? '' : 'bg-problem'}`}>
                    <div className='row'>
                         {/* <div className='col-md-2'>
                         <div className="user-profile">
                              <img src={`https://ui-avatars.com/api/?name=${item.userName}`} alt="avatars" />
                              <div className="user-name">
                                   <h4>{item.userName}</h4>
                                   <p>{item?.isHighlit ? 'Highlit' : 'Not Highlighted'}</p>
                              </div>
                         </div>

                        
                    </div> */}

                         <div className='col-md-12'>
                              <div className="problem-content">
                                   <div className="badge-row">
                                        <span className="cat-pill">{item.type}</span>
                                        <span className="status-dot">{item.visibility} - {item.category}</span>
                                   </div>

                                   <h3>{item.title}</h3>

                                   <p className='mb-0'> Published: {formatCreatedAt(item.createdAt)}
                                        {item?.isEdited && (
                                             <span className='d-inline-block ms-2'>- Edited on {formatCreatedAt(item?.editedAt)}</span>
                                        )}
                                   </p>


                                   <div className="engagement-metrics">
                                        <div className="m-item">
                                             <span><ThumbUpAlt /></span>
                                             <strong>{item.likes || 0}</strong>
                                        </div>

                                        <div className="m-item">
                                             <span><ChatBubble /></span>
                                             <strong>{item.comments || 0}</strong>
                                        </div>
                                        <div className="m-item">
                                             <span><StarBorderPurple500 /></span>
                                             <strong>{item?.interestedCount || 0}</strong>
                                        </div>
                                        <div className="m-item">
                                             <span><BookmarkBorder /></span>
                                             <strong>{item?.bookmarkCount || 0}</strong>
                                        </div>
                                   </div>


                                   <div className="action-set">



                                        <Link to={`/interested/${item?.id}`} className="primary-action">Launch Details</Link>

                                        {user?.uid === item?.uid && (
                                             <button className="edit-btn" onClick={() => setEditItem(item)}>
                                                  <EditNote /> Edit
                                             </button>
                                        )}
                                        <button onClick={() => shareTextOf(item.problem)} className="alt-action">
                                             <Share />
                                        </button>
                                   </div>
                              </div>
                         </div>
                    </div>
               </div>
          </div>
     );

     // ================= NEW UI =================

     const renderLikedCard = (item, i) => (
          <div key={i} className="liked-card">
               <img src={`https://ui-avatars.com/api/?name=${item.userName}`} alt={`avatars ${item.userName}`} />
               <div>

                    <h4>{item.userName} ❤️ -  <span>{item.category}</span></h4>
                    <h3>{item?.title}</h3>
                    <p>{item.problem?.slice(0, 120)}...</p>
                    <Link className='btn-jkse' to={`/interested/${item.id}`} >View</Link>

               </div>
          </div>
     );

     const renderBookmarkCard = (item, i) => (
          <div key={i} className="bookmark-card">
               <div>
                    <span>{item.category}</span>
                    <h3>{item.problem?.slice(0, 100)}...</h3>
               </div>
               <Link className='btn-jkse' to={`/interested/${item.id}`} >View</Link>
          </div>
     );

     const renderCommentCard = (c, i) => (
          <div key={i} className="comment-card">
               <div className="comment-head">
                    <img src={`https://ui-avatars.com/api/?name=${c.userName || 'User'}`} alt={`avatars ${c.userName}`} />
                    <div>
                         <h4>{c.userName || 'User'}</h4>
                         <span>{formatCreatedAt(c.createdAt)}</span>
                    </div>
               </div>

               <p>{c.text}</p>

               <Link className='btn-view' to={`/interested/${c.problemId}`} >View</Link>
          </div>
     );

     // ================= UI =================

     const stats = [
          {
               label: 'Total Posts',
               count: totalProblems,
               growth: 'UPDATE',
               icon: <RocketLaunch />
          },
          {
               label: 'Your Posts',
               count: problems?.length,
               growth: 'SUBMIT',
               icon: <Lightbulb />
          },
          {
               label: 'Public Posts',
               count: problems?.filter(p => p.visibility === 'Public').length,
               growth: 'YOUR',
               icon: <DynamicFeed />
          },
          {
               label: 'Private Posts',
               count: problems?.filter(p => p.visibility === 'Private').length,
               growth: 'YOUR',
               icon: <DynamicFeed />
          },
          {
               label: 'Your Interest',
               count: totalInterested,
               growth: 'LIVE',
               icon: <Whatshot />
          },
          {
               label: 'Liked',
               count: likedProblems?.length,
               growth: 'LOVE',
               icon: <ThumbUpAlt />
          },
          {
               label: 'Bookmarks',
               count: bookmarkedProblems?.length,
               growth: 'SAVE',
               icon: <StarBorderPurple500 />
          },
          {
               label: 'Comments',
               count: comments?.length,
               growth: 'WORK',
               icon: <ChatBubble />
          },
     ];


     return (
          <>
               <PageShapes />
               <div className="new-dashboard">
                    <div className="main-wrapper">

                         {/* TOP CARDS */}
                         <section className="analytics-strip">
                              <div className='row row-gap-3'>
                                   {stats.map((s, i) => (
                                        <div className='col-md-3 col-6'>
                                             <div key={i} className="mini-stat">
                                                  <div className="stat-icon">{s.icon}</div>
                                                  <div className="stat-txt">
                                                       <span className="count">{s.count || 0}</span>
                                                       <span className="label">{s.label}</span>
                                                  </div>
                                                  <div className="growth-tag">{s.growth}</div>
                                             </div>
                                        </div>
                                   ))}
                              </div>
                         </section>

                         {/* TABS */}
                         <section className="data-table-container">
                              <div className="table-header">
                                   <h2>Tracked <span>Ventures</span></h2>
                              </div>

                              <Tabs>
                                   <TabList>
                                        <Tab>Public Items</Tab>
                                        <Tab>Private Items</Tab>
                                        <Tab>Liked</Tab>
                                        <Tab>Bookmarks</Tab>
                                        <Tab>Comments</Tab>
                                        {isAdmin && <Tab><AdminPanelSettings style={{ fontSize: 14, verticalAlign: 'middle' }} /> Admin</Tab>}
                                   </TabList>

                                   <TabPanel>
                                        <div className="row row-gap-3">
                                             {loading
                                                  ? <Loading text="Loading..." />
                                                  : problems.filter(p => p.visibility === 'Public').length === 0
                                                       ? <Loading text="No public Posts yet" />
                                                       : problems.filter(p => p.visibility === 'Public').map(renderProblemCard)}
                                        </div>
                                   </TabPanel>

                                   <TabPanel>
                                        <div className="row row-gap-3">
                                             {loading
                                                  ? <Loading text="Loading..." />
                                                  : problems.filter(p => p.visibility === 'Private').length === 0
                                                       ? <Loading text="No private Posts yet" />
                                                       : problems.filter(p => p.visibility === 'Private').map(renderProblemCard)}
                                        </div>
                                   </TabPanel>

                                   <TabPanel>
                                        <div className="rows-container">
                                             {likedProblems.length === 0
                                                  ? <Loading text="No liked Posts" />
                                                  : likedProblems.map(renderLikedCard)}
                                        </div>
                                   </TabPanel>

                                   <TabPanel>
                                        <div className="rows-container">
                                             {bookmarkedProblems.length === 0
                                                  ? <Loading text="No bookmarks" />
                                                  : bookmarkedProblems.map(renderBookmarkCard)}
                                        </div>
                                   </TabPanel>

                                   <TabPanel>
                                        <div className="rows-container">
                                             {comments.length === 0
                                                  ? <Loading text="No comments" />
                                                  : comments.map(renderCommentCard)}
                                        </div>
                                   </TabPanel>

                                   {isAdmin && (
                                        <TabPanel>
                                             <div className="row row-gap-3">
                                                  {loading
                                                       ? <Loading text="Loading..." />
                                                       : allProblems.length === 0
                                                            ? <Loading text="No problems found" />
                                                            : allProblems.map((item, i) => (
                                                                 <div key={i} className="col-xl-4 col-md-6">
                                                                      <AdminCard item={item} />
                                                                 </div>
                                                            ))}
                                             </div>
                                        </TabPanel>
                                   )}

                              </Tabs>
                         </section>

                    </div>
               </div>

               <EditProblemOffcanvas
                    show={!!editItem}
                    handleClose={() => setEditItem(null)}
                    problemData={editItem}
                    onUpdated={(updated) => {
                         setProblems(prev => prev.map(p => p.id === editItem?.id ? { ...p, ...updated } : p));
                         setEditItem(null);
                    }}
               />
          </>
     );
};

export default OverView;